import { useState } from "react";
import { HardDrive, Plus, X } from "lucide-react";
import { ApiError } from "@/lib/api";
import { Badge, Button, Select } from "@/components/ui/primitives";
import { useAuth } from "@/features/auth/auth-context";
import {
  useAssets,
  useLinkTicketAsset,
  useTicketAssets,
  useUnlinkTicketAsset,
} from "@/features/assets/hooks";
import {
  ASSET_STATUS_LABELS,
  ASSET_TYPE_LABELS,
  expiryBadge,
} from "@/features/assets/labels";

/**
 * Assets involved in a ticket, shown in the ticket detail. Staff with
 * `ticket:update` can attach any of the customer's assets (the picker is
 * scoped to that customer, and the server re-checks the match); everyone with
 * `asset:read` sees the list.
 */
export function TicketAssetsPanel({
  ticketId,
  customerId,
}: {
  ticketId: string;
  customerId: string;
}) {
  const { can } = useAuth();
  const canManage = can("ticket:update");
  const { data: linked } = useTicketAssets(ticketId);
  const link = useLinkTicketAsset(ticketId);
  const unlink = useUnlinkTicketAsset(ticketId);

  // The customer's assets, to pick from. Only fetched for staff who can link.
  const { data: pool } = useAssets(
    { customer_id: customerId, page_size: 100 },
    { enabled: canManage },
  );

  const [choice, setChoice] = useState("");
  const [error, setError] = useState<string | null>(null);

  const linkedIds = new Set((linked ?? []).map((a) => a.id));
  const available = (pool?.items ?? []).filter((a) => !linkedIds.has(a.id));

  async function run(fn: () => Promise<unknown>) {
    setError(null);
    try {
      await fn();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Something went wrong.");
    }
  }

  return (
    <section className="rounded-md border border-border">
      <div className="flex items-center gap-2 border-b border-border px-3 py-2 text-sm font-medium">
        <HardDrive className="h-4 w-4" aria-hidden="true" />
        Assets
      </div>

      <div className="space-y-2 p-3">
        {error && (
          <div role="alert" className="rounded-md bg-destructive/10 px-3 py-1.5 text-xs text-destructive">
            {error}
          </div>
        )}

        {(linked ?? []).length === 0 && (
          <p className="text-xs text-muted-foreground">No assets linked to this ticket.</p>
        )}

        {(linked ?? []).map((a) => {
          const warranty = expiryBadge(a.warranty_status, null);
          return (
            <div
              key={a.id}
              className="flex items-center justify-between gap-2 rounded-md border border-border px-3 py-2"
            >
              <div className="min-w-0">
                <div className="truncate text-sm font-medium">{a.name}</div>
                <div className="truncate text-xs text-muted-foreground">
                  {a.asset_tag} · {ASSET_TYPE_LABELS[a.asset_type]} · {ASSET_STATUS_LABELS[a.status]}
                  {a.serial_number ? ` · ${a.serial_number}` : ""}
                </div>
              </div>
              <div className="flex shrink-0 items-center gap-2">
                <Badge tone={warranty.tone}>{warranty.label}</Badge>
                {canManage && (
                  <button
                    type="button"
                    aria-label={`Unlink ${a.name}`}
                    className="rounded-md p-1 hover:bg-accent"
                    onClick={() => void run(() => unlink.mutateAsync(a.id))}
                  >
                    <X className="h-4 w-4" />
                  </button>
                )}
              </div>
            </div>
          );
        })}

        {canManage && (
          <div className="flex items-center gap-2 pt-1">
            <Select
              aria-label="Choose an asset to link"
              value={choice}
              onChange={(e) => setChoice(e.target.value)}
            >
              <option value="">
                {available.length ? "Link an asset…" : "No more assets to link"}
              </option>
              {available.map((a) => (
                <option key={a.id} value={a.id}>
                  {a.asset_tag} — {a.name}
                </option>
              ))}
            </Select>
            <Button
              type="button"
              variant="outline"
              disabled={!choice || link.isPending}
              onClick={() =>
                void run(async () => {
                  await link.mutateAsync(choice);
                  setChoice("");
                })
              }
            >
              <Plus className="h-4 w-4" aria-hidden="true" />
              Link
            </Button>
          </div>
        )}
      </div>
    </section>
  );
}
